import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Modal, Separator, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import type { SalesTransaction } from '@/api/models';
import { formatGel, getNodeTestIdProp, getNodeTestProps } from '@/utils';
import {
    formatTransactionId,
    getStatusBadgeKind,
    isDiscounted,
    PAYMENT_META,
    STATUS_BADGE_META,
} from '../../transactionsMeta';
import { TransactionDetailLine } from './TransactionDetailLine';
import { TransactionDetailTotals } from './TransactionDetailTotals';

type TransactionDetailModalProps = {
    isOpen: boolean;
    onClose: () => void;
    transaction: SalesTransaction | null;
};

/**
 * Read-only detail for one transaction (render-only). The grid keeps the full
 * payload, so the modal reads everything from the `transaction` prop — no extra
 * fetch. Mirrors the bos-app Sales transactions detail modal: header (id +
 * status + date + seller, and the sale's discounts when it has any), the cart
 * lines (`TransactionDetailLine`), the money breakdown
 * (`TransactionDetailTotals`), the payment split, and the optional promo /
 * refund note. A sale without discounts looks as it did before SEL-100.
 */
export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
    isOpen,
    onClose,
    transaction,
}) => {
    const { t } = useTranslation('pages');

    if (!transaction) {
        return null;
    }

    const badge = STATUS_BADGE_META[getStatusBadgeKind(transaction)];
    const dateLabel = dayjs(transaction.transactionDate).format(
        'D MMM YYYY · HH:mm',
    );

    return (
        <Modal.Backdrop
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
            variant="blur"
        >
            <Modal.Container placement="center" size="md">
                <Modal.Dialog {...getNodeTestProps('transaction-detail')}>
                    <Modal.CloseTrigger />
                    <Modal.Header>
                        <Modal.Icon>
                            <Icon
                                className="size-5"
                                icon="solar:bill-list-bold-duotone"
                            />
                        </Modal.Icon>
                        <Modal.Heading>
                            {formatTransactionId(transaction.id)}
                        </Modal.Heading>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                    <Chip
                                        color={badge.color}
                                        size="sm"
                                        variant="soft"
                                    >
                                        {t(badge.labelKey)}
                                    </Chip>
                                    <Typography color="muted" type="body-sm">
                                        {dateLabel}
                                    </Typography>
                                </div>
                                <div className="flex flex-col items-end">
                                    <Typography type="body-sm" weight="medium">
                                        {transaction.seller.name}
                                    </Typography>
                                    {isDiscounted(transaction) ? (
                                        <Typography
                                            className="tabular-nums"
                                            {...getNodeTestIdProp(
                                                'transaction-detail-discounts',
                                            )}
                                            type="body-xs"
                                        >
                                            {`${t('transactions.modal.discounts')} −${formatGel(transaction.discountTotal)}`}
                                        </Typography>
                                    ) : null}
                                </div>
                            </div>

                            <Separator />

                            <div className="flex flex-col gap-2">
                                <Typography type="body-sm" weight="semibold">
                                    {t('transactions.modal.cart')}
                                </Typography>
                                <div
                                    className="flex flex-col divide-y divide-border rounded-xl border border-border"
                                    {...getNodeTestProps(
                                        'transaction-detail-cart',
                                    )}
                                >
                                    {transaction.cart.map((item, index) => (
                                        <TransactionDetailLine
                                            index={index}
                                            item={item}
                                            key={`${item.barcode}-${index}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <TransactionDetailTotals
                                transaction={transaction}
                            />

                            <Separator />

                            <div className="flex flex-col gap-2">
                                <Typography type="body-sm" weight="semibold">
                                    {t('transactions.modal.payments')}
                                </Typography>
                                {transaction.payments.map((payment, index) => {
                                    const meta = PAYMENT_META[payment.type];
                                    return (
                                        <div
                                            className="flex items-center justify-between"
                                            key={`${payment.type}-${index}`}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <Icon
                                                    className={`size-4 ${meta.color}`}
                                                    icon={meta.icon}
                                                />
                                                <Typography type="body-sm">
                                                    {t(meta.labelKey)}
                                                </Typography>
                                            </div>
                                            <Typography
                                                className="tabular-nums"
                                                type="body-sm"
                                            >
                                                {formatGel(payment.amount)}
                                            </Typography>
                                        </div>
                                    );
                                })}
                            </div>

                            {transaction.promoCode ? (
                                <div className="flex items-center justify-between">
                                    <Typography color="muted" type="body-sm">
                                        {t('transactions.modal.promo')}
                                    </Typography>
                                    <Typography type="body-sm">
                                        {transaction.promoCode}
                                    </Typography>
                                </div>
                            ) : null}

                            {transaction.refund?.note ? (
                                <div className="rounded-xl bg-surface-secondary p-3">
                                    <Typography color="muted" type="body-xs">
                                        {t('transactions.modal.refundNote')}
                                    </Typography>
                                    <Typography type="body-sm">
                                        {transaction.refund.note}
                                    </Typography>
                                </div>
                            ) : null}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            {...getNodeTestProps('transaction-detail-close')}
                            onPress={onClose}
                            variant="outline"
                        >
                            {t('transactions.modal.close')}
                        </Button>
                    </Modal.Footer>
                </Modal.Dialog>
            </Modal.Container>
        </Modal.Backdrop>
    );
};
